import * as React from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import PostPurchaseService from 'services/PostPurchaseService';
import { STOCK_RULE_LABELS, pacific, usdCents } from './shared';
import { OrderExplanation, OrdersResponse, OrderVerdict, StockDiagnosis } from './types';

// The Orders tab: recent dosaze.com orders, what the upsells did on each, and
// what today's rules say should have happened (collector explain.js), so a
// customer who should have seen an offer and didn't, or the other way round,
// stands out.

const HOURS = [6, 24, 72];
// The most orders the API returns (collector explain.js MAX_ORDERS), the newest first.
const MAX_ORDERS = 500;

const VERDICT: Record<OrderVerdict, { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info'; hint: string }> = {
  matched: { label: 'Matched', color: 'success', hint: 'Offered the upsell it qualifies for.' },
  qualified_not_offered: {
    label: 'Qualified, not offered',
    color: 'warning',
    hint:
      'Qualifies under today\'s rules but no offer was recorded. Usual causes: Shopify skips the page for saved cards, ' +
      'Shop Pay and some wallets; or the upsell was off, staff-only or priced differently when the order was placed.',
  },
  offered_not_qualified: {
    label: 'Offered, does not qualify',
    color: 'error',
    hint: 'Was offered an upsell today\'s rules would not offer. Either a rule or price changed since, or something is wrong.',
  },
  offered_other: { label: 'Offered another', color: 'error', hint: 'Was offered a different upsell than today\'s rules pick first.' },
  none: { label: 'No upsell', color: 'default', hint: 'Qualifies for none, and none was offered.' },
};

// The checkout's reasons (collector rules.js INELIGIBLE, stock.js, explain.js), in words.
const REASONS: Record<string, string> = {
  trigger_units: "doesn't have the items it's for",
  blocking_line: 'already has it, or an item that rules it out',
  not_deal: "the items weren't bought at the deal it needs",
  selling_plan: 'the items are on a subscription',
  wrong_currency: 'another currency',
  no_price_difference: 'nothing to charge',
  above_list_price: 'would cost more than list price',
  no_discount: 'its price would not be a discount',
  target_unavailable: 'the product to add is not for sale',
  target_missing_cogs: 'the product to add has no COGS',
  target_not_in_store: 'the product to add is not in the store',
  target_ambiguous: 'two active products share the SKU to add',
  switched_off: 'switched off',
  staff_only: 'staff only',
  another_offer_first: 'another upsell comes first',
  conflicts_with_earlier_offer: 'conflicts with an earlier offer',
  // collector stock.js STOCK_REFUSALS
  stock_no_shipping_location: 'no shipping location for the stock check',
  stock_no_center_holds_trigger: 'no ShipBob center holds the items it is for',
  stock_not_at_nearest_center: 'not in stock at the center the order ships from',
  stock_not_in_region: "not in stock in the customer's region",
  stock_not_in_country: 'not in stock at any ShipBob center',
  stock_unknown: 'ShipBob stock could not be read',
};

const centerList = (centers: StockDiagnosis['stocked']) =>
  centers.length
    ? centers.map(center => `${center.center} (${center.fulfillable}${center.miles == null ? '' : `, ${center.miles.toLocaleString()} mi`})`).join(', ')
    : 'none';

// Where an upsell's product would ship from, or why the stock check refuses it,
// from the centers that hold it and the upsell's rule.
function StockLine({ stock }: { stock: StockDiagnosis }) {
  const rule = `${STOCK_RULE_LABELS[stock.rule] ?? stock.rule} rule`;
  const where = [stock.region && `customer in the ${stock.region}`, stock.homeCenter && `order's nearest center ${stock.homeCenter}`]
    .filter(Boolean)
    .join('; ');
  return (
    <Typography variant="caption" component="div" color="text.secondary" sx={{ pl: 3 }}>
      📦 {stock.units}× {stock.sku} · {rule}{where ? ` · ${where}` : ''}
      <br />
      {stock.rule === 'off'
        ? 'No stock check.'
        : stock.refusal
          ? `Refused: ${REASONS[stock.refusal] ?? stock.refusal}${stock.error ? ` (${stock.error})` : ''}.`
          : `Would ship from ${stock.eligible[0]?.center ?? '?'}${stock.eligible.length > 1 ? ` (also allowed: ${stock.eligible.slice(1).map(center => center.center).join(', ')})` : ''}.`}{' '}
      In stock at: {centerList(stock.stocked)}.
    </Typography>
  );
}

// The center the order's first expected upsell would ship from.
function shipsFrom(order: OrderExplanation) {
  const decision = order.decisions.find(entry => entry.upsellId === order.expected[0]);
  return decision?.stock?.[0]?.eligible[0]?.center ?? null;
}

const OUTCOME: Record<string, string> = {
  offered: 'offered',
  viewed: 'viewed, no answer',
  declined: 'declined',
  failed: 'payment failed',
  accepted: 'accepted',
  swapped: 'accepted, swapped',
  added: 'accepted, added',
  swap_failed: 'accepted, swap failed',
};

type Filter = 'all' | 'issues' | 'took' | OrderVerdict;

// Outcomes where the customer accepted (and paid).
const TOOK = new Set(['accepted', 'added', 'swapped', 'swap_failed']);
const tookUpsell = (order: OrderExplanation) => order.offered.some(offer => TOOK.has(offer.outcome ?? ''));

type SortKey = 'placed' | 'shouldGet' | 'got' | 'payment' | 'verdict';
const SORTS: Record<SortKey, (order: OrderExplanation) => string> = {
  placed: order => order.createdAt,
  shouldGet: order => order.expected[0] ?? '',
  got: order => order.offered.map(offer => offer.upsellId).join(','),
  payment: order => order.paymentGateways.join(','),
  verdict: order => VERDICT[order.verdict].label,
};
const PAGE_SIZES = [25, 50, 100];

function OrderRow({ order, adminUrl }: { order: OrderExplanation; adminUrl: (id: number) => string | undefined }) {
  const [open, setOpen] = React.useState(false);
  const verdict = VERDICT[order.verdict];
  return (
    <>
      <TableRow hover onClick={() => setOpen(current => !current)} sx={{ cursor: 'pointer', '& td': { borderBottom: open ? 0 : undefined } }}>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <a href={adminUrl(order.orderId)} target="_blank" rel="noreferrer" onClick={event => event.stopPropagation()}>
            {order.orderName}
          </a>
          {order.cancelled && <Chip size="small" label="cancelled" sx={{ ml: 0.5 }} />}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{pacific(order.createdAt)}</TableCell>
        <TableCell sx={{ fontSize: 12, maxWidth: 360 }}>
          {order.items.map(item => `${item.quantity}× ${item.sku ?? '(no SKU)'}`).join(' + ')}
        </TableCell>
        <TableCell>
          {order.expected[0] ?? '—'}
          {shipsFrom(order) && (
            <Typography variant="caption" component="div" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              from {shipsFrom(order)}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {order.offered.length
            ? order.offered.map(offer => `${offer.upsellId}: ${OUTCOME[offer.outcome ?? ''] ?? offer.outcome ?? '?'}`).join(', ')
            : '—'}
        </TableCell>
        <TableCell sx={{ fontSize: 12 }}>{order.paymentGateways.join(', ') || '—'}</TableCell>
        <TableCell>
          <Chip size="small" label={verdict.label} color={verdict.color} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{verdict.hint}</Typography>
              <Stack spacing={0.5}>
                {order.decisions.map(decision => (
                  <Box key={decision.upsellId}>
                    <Typography variant="body2">
                      {decision.qualifies && !decision.reason ? '✅' : decision.qualifies ? '⚪' : '✗'} <strong>{decision.upsellId}</strong>:{' '}
                      {decision.qualifies && !decision.reason
                        ? `qualifies${decision.chargeCents != null ? ` (${usdCents(decision.chargeCents)})` : ''}`
                        : decision.qualifies
                          ? `qualifies, but ${REASONS[decision.reason ?? ''] ?? decision.reason}`
                          : REASONS[decision.reason ?? ''] ?? decision.reason}
                    </Typography>
                    {decision.stock?.map(stock => <StockLine key={stock.sku} stock={stock} />)}
                  </Box>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                Items as placed: {order.items.map(item => `${item.quantity}× ${item.sku ?? '(no SKU)'} (paid ${usdCents(item.paidCents)})`).join(', ')}.
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export function OrdersTab({ service }: { service: PostPurchaseService }) {
  const [hours, setHours] = React.useState(24);
  const [filter, setFilter] = React.useState<Filter>('issues');
  const [result, setResult] = React.useState<OrdersResponse | null | undefined>(undefined);
  const [upsellFilter, setUpsellFilter] = React.useState('all');
  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState<{ key: SortKey; desc: boolean }>({ key: 'placed', desc: true });
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(PAGE_SIZES[0]);
  React.useEffect(() => {
    let current = true;
    setResult(undefined);
    service.getOrders(hours).then(response => current && setResult(response));
    return () => {
      current = false;
    };
  }, [service, hours]);

  const orders = result?.orders ?? [];
  const oldest = orders.reduce<OrderExplanation | undefined>(
    (found, order) => (!found || new Date(order.createdAt) < new Date(found.createdAt) ? order : found),
    undefined,
  );
  const count = (verdict: OrderVerdict) => orders.filter(order => order.verdict === verdict).length;
  const issues = (order: OrderExplanation) => order.verdict !== 'matched' && order.verdict !== 'none';
  const needle = search.trim().toLowerCase();
  const upsellIds = [...new Set(orders.flatMap(order => [...order.expected, ...order.offered.map(offer => offer.upsellId)]))].sort();
  const shown = orders
    .filter(order => (filter === 'all' ? true : filter === 'issues' ? issues(order) : filter === 'took' ? tookUpsell(order) : order.verdict === filter))
    .filter(order => upsellFilter === 'all' || order.expected.includes(upsellFilter) || order.offered.some(offer => offer.upsellId === upsellFilter))
    .filter(
      order =>
        !needle ||
        [order.orderName, ...order.items.map(item => item.sku ?? ''), ...order.paymentGateways].some(text => text.toLowerCase().includes(needle)),
    )
    .sort((a, b) => {
      const order = SORTS[sort.key](a).localeCompare(SORTS[sort.key](b));
      return sort.desc ? -order : order;
    });
  const lastPage = Math.max(0, Math.ceil(shown.length / pageSize) - 1);
  const shownPage = Math.min(page, lastPage);
  const choose = (next: () => void) => {
    next();
    setPage(0);
  };
  const header = (key: SortKey | null, label: string) =>
    key ? (
      <TableCell sortDirection={sort.key === key ? (sort.desc ? 'desc' : 'asc') : false} sx={{ whiteSpace: 'nowrap' }}>
        <TableSortLabel
          active={sort.key === key}
          direction={sort.key === key && !sort.desc ? 'asc' : 'desc'}
          onClick={() => choose(() => setSort(current => ({ key, desc: current.key === key ? !current.desc : true })))}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    ) : (
      <TableCell>{label}</TableCell>
    );
  const adminUrl = (orderId: number) =>
    result?.domain ? `https://admin.shopify.com/store/${result.domain.replace('.myshopify.com', '')}/orders/${orderId}` : undefined;
  const chips: { value: Filter; label: string }[] = [
    { value: 'issues', label: `Needs a look (${orders.filter(issues).length})` },
    { value: 'qualified_not_offered', label: `Qualified, not offered (${count('qualified_not_offered')})` },
    { value: 'offered_not_qualified', label: `Offered, not qualified (${count('offered_not_qualified')})` },
    { value: 'took', label: `Took an upsell (${orders.filter(tookUpsell).length})` },
    { value: 'matched', label: `Matched (${count('matched')})` },
    { value: 'all', label: `All (${orders.length})` },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2 }} aria-label="orders debugging">
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>Orders</Typography>
        <Select size="small" value={hours} onChange={event => setHours(Number(event.target.value))} inputProps={{ 'aria-label': 'period' }}>
          {HOURS.map(option => (
            <MenuItem key={option} value={option}>Last {option} hours</MenuItem>
          ))}
        </Select>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Recent dosaze.com orders: what each customer bought, which upsell they should get under today's rules and
        switches, and what the tracking says they were offered. Click an order for each upsell's reason and
        the ShipBob centers its products could ship from under its stock rule. Today's rules, prices, switches and
        stock, not those at the time.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        {chips.map(chip => (
          <Chip
            key={chip.value}
            label={chip.label}
            color={filter === chip.value ? 'primary' : 'default'}
            variant={filter === chip.value ? 'filled' : 'outlined'}
            onClick={() => choose(() => setFilter(chip.value))}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
        <Select size="small" value={upsellFilter} onChange={event => choose(() => setUpsellFilter(String(event.target.value)))} inputProps={{ 'aria-label': 'upsell' }}>
          <MenuItem value="all">All upsells</MenuItem>
          {upsellIds.map(id => (
            <MenuItem key={id} value={id}>{id}</MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          placeholder="Search order #, SKU or payment"
          value={search}
          onChange={event => choose(() => setSearch(event.target.value))}
          inputProps={{ 'aria-label': 'search orders' }}
          sx={{ minWidth: 260 }}
        />
      </Box>
      {result === undefined && <LinearProgress aria-label="loading orders" />}
      {result === null && <Alert severity="error">Could not load the orders.</Alert>}
      {orders.length >= MAX_ORDERS && oldest && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Only the newest {MAX_ORDERS} orders are shown, back to {pacific(oldest.createdAt)}.
        </Alert>
      )}
      {result && !shown.length && <Typography variant="body2" color="text.secondary">No orders here.</Typography>}
      {result && shown.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="orders">
            <TableHead>
              <TableRow>
                {header(null, 'Order')}
                {header('placed', 'Placed (Pacific)')}
                {header(null, 'Items')}
                {header('shouldGet', 'Should get')}
                {header('got', 'Got')}
                {header('payment', 'Payment')}
                {header('verdict', 'Verdict')}
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.slice(shownPage * pageSize, (shownPage + 1) * pageSize).map(order => (
                <OrderRow key={order.orderId} order={order} adminUrl={adminUrl} />
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={shown.length}
            page={shownPage}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={PAGE_SIZES}
            onRowsPerPageChange={event => choose(() => setPageSize(Number(event.target.value)))}
          />
        </Box>
      )}
    </Paper>
  );
}
